import React, { useState } from 'react';
import BarcodeScanModal, { BarcodeScanResult } from './BarcodeScanModal';
import { getVaillantSerialNumberFormState } from '../utils/vaillantSerialNumber';

interface SeriennummerModalProps {
  onClose: () => void;
  onSubmit: (serialNumber: string, imageData: string | null) => void;
  initialValue?: string;
}

const SeriennummerModal: React.FC<SeriennummerModalProps> = ({ onClose, onSubmit, initialValue = '' }) => {
  const [imageData, setImageData] = useState<string | null>(null);
  const [serialNumber, setSerialNumber] = useState(initialValue);
  // Bleibt true, sobald einmal ein Foto übernommen wurde – nachträgliches
  // Anpassen der Seriennummer (z. B. Tippfehler korrigieren, oder manuelle
  // Eingabe nach einem Scan ohne erkannten Barcode) soll das Foto-Gate nicht
  // wieder sperren. Seit Ticket #1267 muss der Barcode dabei nicht erkannt
  // worden sein (BarcodeScanModal liefert dafür ggf. eine leere
  // serialNumber) – Pflicht ist nur noch das Foto selbst.
  const [hasPhoto, setHasPhoto] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const { trimmed, formatValid } = getVaillantSerialNumberFormState(serialNumber);
  // Formatabweichungen vom Vaillant-Muster sind nur ein Hinweis, keine Sperre –
  // der Monteur kennt ggf. Sonderfälle, die das Regex nicht abdeckt.
  const canSubmit = trimmed.length > 0 && hasPhoto;

  const handleScan = (result: BarcodeScanResult) => {
    setShowScanner(false);
    setImageData(result.imageData);
    // Leerer serialNumber-Wert bedeutet "kein Barcode erkannt" (Ticket #1267)
    // - dann eine bereits manuell eingetippte Seriennummer nicht überschreiben.
    if (result.serialNumber) {
      setSerialNumber(result.serialNumber);
    }
    setHasPhoto(true);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed, imageData);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4">
        <div className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90dvh] overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Seriennummer Außeneinheit
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 dark:hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Schließen"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

            {/* Bild-Preview */}
            <div
              className="relative w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900"
              style={{ minHeight: '200px' }}
            >
              {imageData ? (
                <img
                  src={imageData}
                  alt="Vorschau Seriennummer"
                  className="w-full h-full object-contain max-h-64"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500 py-8 px-4 text-center">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">Noch kein Foto aufgenommen</span>
                </div>
              )}
            </div>

            {/* Scan-Button */}
            <button
              onClick={() => setShowScanner(true)}
              className="w-full min-h-[48px] flex items-center justify-center gap-2 rounded-xl border-2 border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 font-medium transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h.01M4 12h.01M4 18h.01M8 6h12M8 12h12M8 18h12" />
              </svg>
              {hasPhoto ? 'Erneut scannen' : 'Barcode scannen (optional)'}
            </button>

            {!hasPhoto && (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-4 py-2">
                Ein Foto der Seriennummer ist Pflicht, bevor gespeichert werden kann. Der Barcode muss dabei nicht erkannt werden – die Seriennummer kann auch manuell eingetragen werden.
              </p>
            )}

            {/* Seriennummer Text-Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Seriennummer
              </label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="z. B. 21054500100028300006000001N4"
                className={`w-full rounded-xl border bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 ${
                  trimmed.length > 0 && !formatValid
                    ? 'border-amber-400 dark:border-amber-500 focus:ring-amber-400'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                }`}
              />
              {trimmed.length > 0 && !formatValid && (
                <p className="mt-1.5 text-sm text-amber-700 dark:text-amber-400">
                  Hinweis: Die eingegebene Seriennummer entspricht keinem bekannten Vaillant-Format. Bitte prüfen – ein Senden ist trotzdem möglich.
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full min-h-[52px] rounded-xl font-semibold text-base transition-colors ${
                canSubmit
                  ? 'bg-primary-600 hover:bg-primary-700 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              Senden
            </button>
          </div>
        </div>
      </div>

      {showScanner && (
        <BarcodeScanModal
          isOpen={showScanner}
          onClose={() => setShowScanner(false)}
          onScan={handleScan}
        />
      )}
    </>
  );
};

export default SeriennummerModal;
